import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { addDays, format } from 'date-fns';
import { extras as seedExtras, services as seedServices } from '../data/services';
import { defaultSettings, vehicleSizes } from '../data/schedule';
import { Booking, BookingStatus, Extra, PaymentStatus, Service, Settings, VehicleSizeId } from '../types/booking';
import { depositFor, priceFor } from '../utils/availability';
import { reference } from '../utils/format';
const STORAGE_KEY = 'dcc-studio-v1';
type Store = {
  services: Service[];
  extras: Extra[];
  settings: Settings;
  bookings: Booking[];
};
type NewBooking = {
  serviceId: string;
  extraIds: string[];
  vehicleSizeId: VehicleSizeId;
  date: string;
  time: string;
  paymentMethod: Booking['paymentMethod'];
  customer: Booking['customer'];
  source?: Booking['source'];
};
type Ctx = Store & {
  createBooking: (input: NewBooking) => Booking;
  updateBooking: (id: string, patch: Partial<Booking>) => void;
  setStatus: (id: string, status: BookingStatus) => void;
  setPaymentStatus: (id: string, status: PaymentStatus) => void;
  reschedule: (id: string, date: string, time: string) => void;
  findByReference: (ref: string) => Booking | null;
  saveService: (service: Service) => void;
  saveExtra: (extra: Extra) => void;
  updateSettings: (patch: Partial<Settings>) => void;
  toggleBlockedDate: (iso: string) => void;
  resetDemoData: () => void;
};
const StudioContext = createContext<Ctx | null>(null);
function seedBookings(services: Service[], extras: Extra[], settings: Settings): Booking[] {
  const rows: {
    offset: number;
    time: string;
    serviceId: string;
    extraIds: string[];
    size: VehicleSizeId;
    status: BookingStatus;
    payment: PaymentStatus;
    method: Booking['paymentMethod'];
    customer: Booking['customer'];
  }[] = [{
    offset: 0,
    time: '09:00',
    serviceId: 'showroom',
    extraIds: ['engine'],
    size: 'suv',
    status: 'confirmed',
    payment: 'deposit_paid',
    method: 'deposit',
    customer: {
      name: 'Rick van Dijk',
      email: 'rick@example.nl',
      phone: '06 21 55 98 12',
      car: 'VW Tiguan',
      plate: 'K-812-PL',
      notes: 'Achterbank heeft vlekken van hondenharen.'
    }
  }, {
    offset: 0,
    time: '13:30',
    serviceId: 'interior',
    extraIds: [],
    size: 'sedan',
    status: 'pending',
    payment: 'unpaid',
    method: 'studio',
    customer: {
      name: 'Sanne de Wit',
      email: 'sanne@example.nl',
      phone: '06 48 21 77 03',
      car: 'Audi A4',
      plate: 'H-044-XT',
      notes: ''
    }
  }, {
    offset: 1,
    time: '09:00',
    serviceId: 'paint-correction',
    extraIds: ['ceramic'],
    size: 'sedan',
    status: 'confirmed',
    payment: 'deposit_paid',
    method: 'deposit',
    customer: {
      name: 'Jeroen Bakker',
      email: 'jeroen@example.nl',
      phone: '06 11 90 42 18',
      car: 'BMW 320i',
      plate: 'G-771-RN',
      notes: 'Veel swirls op de motorkap.'
    }
  }, {
    offset: 2,
    time: '10:30',
    serviceId: 'interior',
    extraIds: ['leather'],
    size: 'van',
    status: 'confirmed',
    payment: 'unpaid',
    method: 'studio',
    customer: {
      name: 'Peter Hoogendijk',
      email: 'peter@example.nl',
      phone: '06 30 18 65 44',
      car: 'Ford Transit',
      plate: 'V-329-BD',
      notes: 'Bedrijfsbus, factuur op naam van Hoogendijk Installaties.'
    }
  }, {
    offset: 4,
    time: '14:00',
    serviceId: 'showroom',
    extraIds: [],
    size: 'compact',
    status: 'pending',
    payment: 'unpaid',
    method: 'studio',
    customer: {
      name: 'Fatima el Amrani',
      email: 'fatima@example.nl',
      phone: '06 55 71 20 98',
      car: 'Opel Corsa',
      plate: 'S-205-KJ',
      notes: ''
    }
  }, {
    offset: -3,
    time: '09:30',
    serviceId: 'showroom',
    extraIds: ['engine'],
    size: 'sedan',
    status: 'completed',
    payment: 'paid',
    method: 'deposit',
    customer: {
      name: 'Marieke Jansen',
      email: 'marieke@example.nl',
      phone: '06 27 84 31 60',
      car: 'Volvo V60',
      plate: 'T-118-ZF',
      notes: ''
    }
  }, {
    offset: -6,
    time: '11:00',
    serviceId: 'interior',
    extraIds: [],
    size: 'compact',
    status: 'cancelled',
    payment: 'refunded',
    method: 'deposit',
    customer: {
      name: 'Dennis Kok',
      email: 'dennis@example.nl',
      phone: '06 12 33 87 25',
      car: 'Toyota Aygo',
      plate: 'N-660-QW',
      notes: 'Geannuleerd wegens ziekte.'
    }
  }];
  return rows.map((row, i) => {
    const service = services.find(s => s.id === row.serviceId) ?? services[0];
    const chosen = extras.filter(e => row.extraIds.includes(e.id));
    const size = vehicleSizes.find(v => v.id === row.size) ?? vehicleSizes[0];
    const {
      amount,
      minutes
    } = priceFor(service, chosen, size);
    return {
      id: `seed-${i}`,
      reference: `DCC-${(1024 + i).toString(36).toUpperCase()}X${i}`,
      serviceId: service.id,
      extraIds: row.extraIds,
      vehicleSizeId: row.size,
      date: format(addDays(new Date(), row.offset), 'yyyy-MM-dd'),
      time: row.time,
      durationMinutes: minutes,
      amount,
      depositAmount: depositFor(amount, settings),
      paymentMethod: row.method,
      paymentStatus: row.payment,
      status: row.status,
      customer: row.customer,
      createdAt: new Date(Date.now() - (i + 1) * 86400000).toISOString(),
      source: 'online'
    };
  });
}
function freshStore(): Store {
  const settings = {
    ...defaultSettings
  };
  return {
    services: seedServices,
    extras: seedExtras,
    settings,
    bookings: seedBookings(seedServices, seedExtras, settings)
  };
}
function load(): Store {
  if (typeof window === 'undefined') return freshStore();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return freshStore();
    const parsed = JSON.parse(raw) as Store;
    if (!parsed.bookings || !parsed.services || !parsed.settings) return freshStore();
    return parsed;
  } catch {
    return freshStore();
  }
}
export function StudioProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [store, setStore] = useState<Store>(load);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
      /* storage unavailable — state stays in memory */
    }
  }, [store]);
  const createBooking = useCallback((input: NewBooking) => {
    const service = store.services.find(s => s.id === input.serviceId) ?? store.services[0];
    const chosen = store.extras.filter(e => input.extraIds.includes(e.id));
    const size = vehicleSizes.find(v => v.id === input.vehicleSizeId) ?? vehicleSizes[0];
    const {
      amount,
      minutes
    } = priceFor(service, chosen, size);
    const deposit = depositFor(amount, store.settings);
    const booking: Booking = {
      id: `b-${Date.now()}`,
      reference: reference(),
      serviceId: service.id,
      extraIds: input.extraIds,
      vehicleSizeId: input.vehicleSizeId,
      date: input.date,
      time: input.time,
      durationMinutes: minutes,
      amount,
      depositAmount: deposit,
      paymentMethod: input.paymentMethod,
      paymentStatus: input.paymentMethod === 'deposit' ? 'deposit_paid' : 'unpaid',
      status: input.paymentMethod === 'deposit' ? 'confirmed' : 'pending',
      customer: input.customer,
      createdAt: new Date().toISOString(),
      source: input.source ?? 'online'
    };
    setStore(prev => ({
      ...prev,
      bookings: [booking, ...prev.bookings]
    }));
    return booking;
  }, [store.services, store.extras, store.settings]);
  const updateBooking = useCallback((id: string, patch: Partial<Booking>) => {
    setStore(prev => ({
      ...prev,
      bookings: prev.bookings.map(b => b.id === id ? {
        ...b,
        ...patch
      } : b)
    }));
  }, []);
  const value = useMemo<Ctx>(() => ({
    ...store,
    createBooking,
    updateBooking,
    setStatus: (id, status) => updateBooking(id, {
      status
    }),
    setPaymentStatus: (id, paymentStatus) => updateBooking(id, {
      paymentStatus
    }),
    reschedule: (id, date, time) => updateBooking(id, {
      date,
      time
    }),
    findByReference: ref => store.bookings.find(b => b.reference.toLowerCase() === ref.trim().toLowerCase()) ?? null,
    saveService: service => setStore(prev => ({
      ...prev,
      services: prev.services.map(s => s.id === service.id ? service : s)
    })),
    saveExtra: extra => setStore(prev => ({
      ...prev,
      extras: prev.extras.map(e => e.id === extra.id ? extra : e)
    })),
    updateSettings: patch => setStore(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        ...patch
      }
    })),
    toggleBlockedDate: iso => setStore(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        blockedDates: prev.settings.blockedDates.includes(iso) ? prev.settings.blockedDates.filter(d => d !== iso) : [...prev.settings.blockedDates, iso]
      }
    })),
    resetDemoData: () => setStore(freshStore())
  }), [store, createBooking, updateBooking]);
  return <StudioContext.Provider value={value} data-id="element-757" data-mp="29y6f6qh0eyay">{children}</StudioContext.Provider>;
}
export function useStudio() {
  const ctx = useContext(StudioContext);
  if (!ctx) throw new Error('useStudio must be used inside StudioProvider');
  return ctx;
}